"use client";

import { useState, useEffect } from "react";
import axios from "../api/axios";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import { UserPlus, Eye, EyeOff, User, Mail, Shield, Users } from "lucide-react";

function Register() {
  const [roles, setRoles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [user, setUser] = useState(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const [params] = useSearchParams();
  const editId = params.get("edit");

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
    role_id: "",
    team_id: "",
    teams_can_view: [],
  });

  const teamOptions = teams.map((team) => ({
    value: team.id,
    label: team.name,
  }));

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#F79639" : "#D1D5DB",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(247, 150, 57, 0.2)" : "none",
      "&:hover": {
        borderColor: "#F79639",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#F79639"
        : state.isFocused
        ? "#FED7AA"
        : "white",
      color: state.isSelected ? "white" : "#374151",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#FED7AA",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#9A3412",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#9A3412",
      "&:hover": {
        backgroundColor: "#F79639",
        color: "white",
      },
    }),
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await axios.get("/api/user", { withCredentials: true });
        setUser(userRes.data);
      } catch (err) {
        console.error("Error fetching user:", err);
      }

      try {
        const rolesRes = await axios.get("/api/roles");
        setRoles(rolesRes.data);
      } catch {
        toast.error("Failed to load roles.");
      }

      try {
        const teamsRes = await axios.get("/api/teams");
        setTeams(teamsRes.data);
      } catch {
        toast.error("Failed to load teams.");
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (editId) {
      axios.get(`/api/users/${editId}`).then((res) => {
        const userToEdit = res.data;
        let teamsView = [];
        if (Array.isArray(userToEdit.teams_can_view)) {
          teamsView = userToEdit.teams_can_view;
        } else if (typeof userToEdit.teams_can_view === "string") {
          try {
            teamsView = JSON.parse(userToEdit.teams_can_view || "[]");
          } catch {
            teamsView = [];
          }
        }

        setForm({
          name: userToEdit.name,
          email: userToEdit.email,
          role_id: userToEdit.role_id,
          team_id: userToEdit.team_id || "",
          password: "",
          password_confirmation: "",
          teams_can_view: teamsView,
        });
      });
    } else {
      const newPass = generatePassword();

      const allTeamIds = teams.map((team) => team.id);

      setForm((f) => ({
        ...f,
        password: newPass,
        password_confirmation: newPass,
        teams_can_view: allTeamIds,
      }));
    }
  }, [editId, teams]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleTeamsCanViewChange = (selectedOptions) => {
    setForm({
      ...form,
      teams_can_view: selectedOptions.map((opt) => opt.value),
    });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      await axios.get("/sanctum/csrf-cookie");
      if (editId) {
        await axios.put(`/api/users/${editId}`, form);
        toast.success("User updated successfully!");
      } else {
        await axios.post("/api/register", form);
        toast.success("User created successfully!");
      }
      setForm({
        name: "",
        email: "",
        password: "",
        password_confirmation: "",
        role_id: "",
        team_id: "",
        teams_can_view: [],
      });
    } catch (err) {
      console.error(err.response?.data);
      const message =
        err.response?.data?.message ||
        err.response?.data?.errors?.[
          Object.keys(err.response.data.errors)[0]
        ][0] ||
        "Operation failed.";
      toast.error(message);
    }
  };

  function generatePassword(length = 12) {
    const chars =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~";
    let pass = "";
    for (let i = 0; i < length; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pass;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl mb-4">
            <UserPlus size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {editId ? "Edit User" : "Add New User"}
          </h1>
          <p className="text-gray-600">
            {editId
              ? "Update user information and permissions"
              : "Create a new user account with appropriate permissions"}
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <form onSubmit={handleRegister} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <User size={16} className="mr-2 text-orange-600" />
                  Full Name *
                </label>
                <input
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  required
                />
              </div>

              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Mail size={16} className="mr-2 text-orange-600" />
                  Email Address *
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  required
                />
              </div>
            </div>

            {/* Password Fields - Only for new users */}
            {!editId && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password *
                  </label>
                  <div className="relative">
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-orange-600"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password *
                  </label>
                  <div className="relative">
                    <input
                      name="password_confirmation"
                      type={showPasswordConfirm ? "text" : "password"}
                      value={form.password_confirmation}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowPasswordConfirm(!showPasswordConfirm)
                      }
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-orange-600"
                    >
                      {showPasswordConfirm ? (
                        <EyeOff size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!editId && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                    <Shield size={16} className="mr-2 text-orange-600" />
                    Role *
                  </label>
                  <select
                    name="role_id"
                    value={form.role_id}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                    required
                  >
                    <option value="">Select Role</option>
                    {roles
                      .filter((role) => {
                        if (user?.role_id === 1)
                          return [2, 3].includes(role.role_id);
                        if (user?.role_id === 2) return role.role_id === 3;
                        return false;
                      })
                      .map((role) => (
                        <option key={role.role_id} value={role.role_id}>
                          {role.role_name}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                    <Users size={16} className="mr-2 text-orange-600" />
                    Team *
                  </label>
                  <select
                    name="team_id"
                    value={form.team_id}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                    required
                  >
                    <option value="">Select Team</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Teams Can View */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Users size={16} className="mr-2 text-orange-600" />
                Teams Can View
              </label>
              <Select
                isMulti
                name="teams_can_view"
                options={teamOptions}
                value={teamOptions.filter((option) =>
                  (form.teams_can_view || []).includes(option.value)
                )}
                onChange={handleTeamsCanViewChange}
                className="basic-multi-select"
                classNamePrefix="select"
                styles={customSelectStyles}
                placeholder="Select teams this user can view..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Select which teams this user will have access to view files from
              </p>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-6 border-t border-gray-200">
              <button
                type="submit"
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center space-x-2 shadow-lg"
              >
                <UserPlus size={20} />
                <span>{editId ? "Update User" : "Create User"}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Register;
